import type { Labelled } from './common.js';

/**
 * الأصول الثابتة وإهلاكها.
 *
 * الغرض ليس جردًا للأجهزة — ذلك جدول بيانات يكفي — بل سدّ ثغرة في الدفاتر:
 * بلا إهلاك مرحَّل تخلو قائمة الدخل من مصروف حقيقي، فيظهر الربح أعلى مما هو
 * في كل شهر ولكل عميل.
 */

/* ==========================================================================
   طرق الإهلاك
   ========================================================================== */

export const DEPRECIATION_METHODS = ['STRAIGHT_LINE', 'DECLINING', 'NONE'] as const;
export type DepreciationMethod = (typeof DEPRECIATION_METHODS)[number];

export interface DepreciationMethodDef extends Labelled<DepreciationMethod> {
  description: string;
}

export const DEPRECIATION_METHOD_DEFS: readonly DepreciationMethodDef[] = [
  {
    key: 'STRAIGHT_LINE',
    label: 'قسط ثابت',
    kind: 'info',
    description: 'قسط متساوٍ كل شهر حتى تبلغ القيمة الدفترية قيمة الخردة.',
  },
  {
    key: 'DECLINING',
    label: 'قسط متناقص',
    kind: 'info',
    description: 'نسبة من القيمة الدفترية المتبقية، فيكبر القسط أول العمر ويصغر آخره.',
  },
  {
    key: 'NONE',
    label: 'لا يُهلك',
    kind: 'neutral',
    description: 'للأراضي والأعمال الفنية وما لا تنقص قيمته بالاستعمال.',
  },
] as const;

export const DEPRECIATION_METHOD_MAP = Object.fromEntries(
  DEPRECIATION_METHOD_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<DepreciationMethod, DepreciationMethodDef>>;

/* ==========================================================================
   حالة الأصل
   ========================================================================== */

export const ASSET_STATUSES = [
  'ACTIVE',
  'UNDER_MAINTENANCE',
  'DISPOSED',
  'WRITTEN_OFF',
] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];

export interface AssetStatusDef extends Labelled<AssetStatus> {
  /** خرج من الدفاتر: لا يُهلك ولا يدخل الجرد. */
  retired: boolean;
}

export const ASSET_STATUS_DEFS: readonly AssetStatusDef[] = [
  { key: 'ACTIVE', label: 'في الخدمة', kind: 'success', retired: false },
  { key: 'UNDER_MAINTENANCE', label: 'تحت الصيانة', kind: 'warning', retired: false },
  { key: 'DISPOSED', label: 'مستبعد', kind: 'neutral', retired: true },
  { key: 'WRITTEN_OFF', label: 'مُعدم', kind: 'danger', retired: true },
] as const;

export const ASSET_STATUS_MAP = Object.fromEntries(
  ASSET_STATUS_DEFS.map((d) => [d.key, d]),
) as Readonly<Record<AssetStatus, AssetStatusDef>>;

/**
 * الأصول التي تدخل دورة الإهلاك.
 *
 * التي تحت الصيانة تُهلك أيضًا: مرور الوقت ينقص قيمتها سواء استُعملت أم لا،
 * وإيقاف إهلاكها شهرًا يجعل مجموع ما أُهلك مخالفًا لعمرها الفعلي.
 */
export const DEPRECIABLE_STATUSES = ASSET_STATUS_DEFS.filter((d) => !d.retired).map(
  (d) => d.key,
) as readonly AssetStatus[];

export const ASSET_COUNT_STATUSES = ['OPEN', 'CLOSED'] as const;
export type AssetCountStatus = (typeof ASSET_COUNT_STATUSES)[number];

export const ASSET_COUNT_STATUS_MAP: Readonly<
  Record<AssetCountStatus, Labelled<AssetCountStatus>>
> = {
  OPEN: { key: 'OPEN', label: 'جارٍ', kind: 'info' },
  CLOSED: { key: 'CLOSED', label: 'مُغلق', kind: 'neutral' },
};

/* ==========================================================================
   نسب الإهلاك المقترحة
   ========================================================================== */

export interface AssetCategorySuggestion {
  name: string;
  rate: number;
  method: DepreciationMethod;
  usefulLifeMonths: number;
  /** رمز حساب الأصل في الدليل الافتراضي. */
  assetCode: string;
}

/**
 * تصنيفات مقترحة بنسب شائعة في مصر.
 *
 * تُقترح ولا تُفرض: النسبة الضريبية قد تخالف النسبة الدفترية، والاختيار
 * بينهما قرار محاسب الشركة لا قرار النظام. القانون المصري ٩١ لسنة ٢٠٠٥
 * يحدد نسبًا للأغراض الضريبية، وهذه أقرب ما تكون إليها.
 */
export const SUGGESTED_ASSET_CATEGORIES: readonly AssetCategorySuggestion[] = [
  {
    name: 'أجهزة حاسب وبرمجيات',
    rate: 50,
    method: 'DECLINING',
    usefulLifeMonths: 24,
    assetCode: '1220',
  },
  {
    name: 'أثاث وتجهيزات مكتبية',
    rate: 25,
    method: 'DECLINING',
    usefulLifeMonths: 48,
    assetCode: '1210',
  },
  {
    name: 'معدات وأجهزة تصوير',
    rate: 25,
    method: 'DECLINING',
    usefulLifeMonths: 48,
    assetCode: '1220',
  },
  {
    name: 'سيارات ووسائل نقل',
    rate: 25,
    method: 'DECLINING',
    usefulLifeMonths: 60,
    assetCode: '1230',
  },
] as const;

/* ==========================================================================
   الحساب
   ========================================================================== */

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

export interface DepreciationInput {
  purchaseCost: number;
  salvageValue: number;
  accumulated: number;
  method: DepreciationMethod;
  /** النسبة السنوية المئوية. */
  rate: number;
  usefulLifeMonths: number;
}

/** القيمة الدفترية = التكلفة ناقص ما أُهلك. */
export function bookValue(purchaseCost: number, accumulated: number): number {
  return round2(purchaseCost - accumulated);
}

/** أقصى ما يمكن إهلاكه على مدى العمر كله. */
export function depreciableBase(purchaseCost: number, salvageValue: number): number {
  return round2(Math.max(purchaseCost - salvageValue, 0));
}

/**
 * قسط شهر واحد.
 *
 * القاعدة الحاكمة: لا يُهلك الأصل تحت قيمة الخردة مهما طال عمره. القسط
 * الأخير يُقصر على ما تبقّى بالضبط.
 *
 * وبدون هذا القصر يستمر القسط الثابت في الخصم إلى ما لا نهاية فتصير القيمة
 * الدفترية سالبة، ويظل المتناقص يقترب من الصفر بلا أن يبلغه فتبقى قروش
 * معلّقة في الدفاتر إلى الأبد.
 */
export function monthlyDepreciation(input: DepreciationInput): number {
  if (input.method === 'NONE') return 0;

  const base = depreciableBase(input.purchaseCost, input.salvageValue);
  const remaining = round2(base - input.accumulated);
  if (remaining <= 0) return 0;

  const raw =
    input.method === 'STRAIGHT_LINE'
      ? base / Math.max(input.usefulLifeMonths, 1)
      : /*
         * المتناقص يُحسب من القيمة الدفترية لا من الأساس.
         *
         * وهذا هو الفرق الجوهري بين الطريقتين: القسط يصغر كل شهر لأن ما
         * يُحسب عليه ينقص، فيتحمّل أول العمر العبء الأكبر — وهو أقرب إلى
         * تناقص القيمة الحقيقي للأجهزة.
         */
        (bookValue(input.purchaseCost, input.accumulated) * (input.rate / 100)) / 12;

  return round2(Math.min(raw, remaining));
}

/** هل بلغ الأصل نهاية إهلاكه. */
export function fullyDepreciated(input: DepreciationInput): boolean {
  return monthlyDepreciation(input) <= 0;
}

/**
 * ربح أو خسارة الاستبعاد.
 *
 * موجب يعني ربحًا وسالب خسارة: الفرق بين ما قُبض والقيمة الدفترية وقت البيع.
 */
export function disposalResult(
  purchaseCost: number,
  accumulated: number,
  proceeds: number,
): number {
  return round2(proceeds - bookValue(purchaseCost, accumulated));
}

/** أول يوم في شهر تاريخ ما، بتوقيت UTC. */
export function periodOf(value: Date | string): Date {
  const d = value instanceof Date ? value : new Date(`${String(value).slice(0, 10)}T00:00:00.000Z`);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));
}

/**
 * شهور الإهلاك المستحقة لأصل.
 *
 * من الشهر التالي لآخر ما رُحِّل، أو من شهر الشراء إن لم يُرحَّل شيء بعد.
 *
 * والإهلاك يبدأ من شهر الشراء نفسه لا من الذي يليه: هذا هو العرف الشائع،
 * وتأجيله شهرًا يجعل مجموع ما أُهلك أقل من عمر الأصل بشهر دائمًا.
 */
export function dueDepreciationPeriods(
  purchaseDate: Date | string,
  lastPeriod: Date | string | null,
  until: Date | string,
  limit = 60,
): Date[] {
  const first = lastPeriod
    ? new Date(Date.UTC(periodOf(lastPeriod).getUTCFullYear(), periodOf(lastPeriod).getUTCMonth() + 1, 1))
    : periodOf(purchaseDate);

  const stop = periodOf(until);
  const out: Date[] = [];
  let cursor = first;

  while (cursor <= stop && out.length < limit) {
    out.push(cursor);
    cursor = new Date(Date.UTC(cursor.getUTCFullYear(), cursor.getUTCMonth() + 1, 1));
  }

  return out;
}
